import { defineComponent as _defineComponent } from 'vue'
import {
  SdsProduct,
  SdsSection8ExposureControl,
} from '@/interfaces/model/general/sds';
import { getSection8OrganizationList } from '@/lib/formItem';
import { defineMessages } from '@/lib/i18n';
import sharedMessages from '@/lib/shared-messages';
import { i18n } from '@/plugins/vue-i18n';
import { now } from '@/utils/datetime';
import OtherTable from '@/views/dashboard/sdsDrawer/section8/OtherTable.vue';
import SdsSection8ACGIHForm from '@/views/dashboard/sdsDrawer/section8/SdsSection8ACGIHForm.vue';
import SdsSection8DNELDMELForm from '@/views/dashboard/sdsDrawer/section8/SdsSection8DNELDMELForm.vue';
import SdsSection8JSOHForm from '@/views/dashboard/sdsDrawer/section8/SdsSection8JSOHForm.vue';
import AcgihTable from '@/views/dashboard/sdsDrawer/section8/AcgihTable.vue';
import DnelDmelTable from '@/views/dashboard/sdsDrawer/section8/DnelDmelTable.vue';
import JsohTable from '@/views/dashboard/sdsDrawer/section8/JsohTable.vue';
import MaterialNameAndCasForm from '@/views/dashboard/sdsDrawer/section8/MaterialNameAndCasForm.vue';
import OrganizationForm from '@/views/dashboard/sdsDrawer/section8/OrganizationForm.vue';
import SdsSection8OTHERForm from '@/views/dashboard/sdsDrawer/section8/SdsSection8OTHERForm.vue';
import { computed, ref, watch } from 'vue';

type Props = {
  isEditing: boolean;
  products?: SdsProduct[];
  exposureControls?: SdsSection8ExposureControl[];
};

export default /*#__PURE__*/_defineComponent({
  __name: 'Section8ExposureControl',
  props: {
    isEditing: { type: Boolean },
    products: { default: () => [] },
    exposureControls: { default: () => [] }
  },
  emits: ["update:exposureControls"],
  setup(__props: any, { emit: emits }: { emit: ({
  (e: 'update:exposureControls', data: SdsSection8ExposureControl[]): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { isEditing: boolean, products: SdsProduct[], exposureControls: SdsSection8ExposureControl[] };




const isValidForm = ref(false);
const form = ref<SdsSection8ExposureControl>();

const groupedExposureControlMap = computed(() => {
  const init: [string, SdsSection8ExposureControl[]][] =
    getSection8OrganizationList().map((o) => {
      return [`${o.value}`, []];
    });
  const map = new Map<string, SdsSection8ExposureControl[]>(init);
  props.exposureControls.forEach((exposureControl) => {
    const key = exposureControl.organization_id;
    if (!map.has(key)) {
      map.set(key, []);
    }
    map.get(key)?.push(exposureControl);
  });
  return map;
});

const $t = i18n.t.bind(i18n);
const m = defineMessages({
  addDataTitle: '暴露限界値に追加するデータを入力してください',
  ACGIH: 'ACGIH',
  JSOH: '日本産業衛生学会 ; Japanese Society of Occupational Health',
  DNEL_DMEL: 'DNEL・DMEL',
  NIOSH: 'NIOSH',
  OSHA: 'OSHA',
  AIHA: 'AIHA',
  SCOEL: 'SCOEL',
  ECHA: 'ECHA',
  DFG: 'DFG',
  ANSES: 'ANSES',
  HSE: 'HSE',
  OTHER: sharedMessages.other,
  NONE: '記載なし',
} as Record<string, string>);

const showDeleteModal = ref(false);
const showEditModal = ref(false);
const showRescueModal = ref(false);
const showAddModal = ref(false);

const selectedInitial = {
  organizationId: '',
  index: -1,
} as const;
const selected = ref<{ organizationId: string; index: number }>({
  ...selectedInitial,
});

watch(
  () => form.value?.organization_id,
  (newValue, oldValue) => {
    if (!form.value || newValue === oldValue || oldValue === undefined) {
      return;
    }

    form.value.category_id = null;
    form.value.property_id = null;
    form.value.category_dust_id = null;
    form.value.category_dust_respirable_id = null;
    form.value.category_concentration_id = null;
    form.value.category_user_id = null;
    form.value.category_term_id = null;
    form.value.category_dust_route_id = null;
    form.value.category_measurement_id = null;

    if (newValue === 'ACGIH') {
      form.value.category_id = 0;
      form.value.property_id = 0;
      return;
    }
    if (newValue === 'JSOH') {
      form.value.category_dust_id = 0;
      form.value.category_dust_respirable_id = 0;
      form.value.category_concentration_id = 0;
      return;
    }
    if (newValue === 'DNEL_DMEL') {
      form.value.category_user_id = 0;
      form.value.category_term_id = 0;
      form.value.category_dust_route_id = 0;
      form.value.category_measurement_id = 0;
      return;
    }
    if (newValue === 'OTHER') {
      form.value.category_id = 0;
      form.value.property_id = 0;
      form.value.category_dust_id = 0;
      form.value.category_dust_respirable_id = 0;
      form.value.category_concentration_id = 0;
      form.value.category_user_id = 0;
      form.value.category_term_id = 0;
      form.value.category_dust_route_id = 0;
      form.value.category_measurement_id = 0;
    }
  }
);

const handleDelete = (organizationId: string, index: number) => {
  selected.value = {
    organizationId,
    index,
  };
  showDeleteModal.value = true;
};

const handleRescue = (organizationId: string, index: number) => {
  selected.value = {
    organizationId,
    index,
  };
  showRescueModal.value = true;
};

const parseToForm = () => {
  const exposureControls = groupedExposureControlMap.value.get(
    selected.value.organizationId
  );
  if (!exposureControls) {
    throw new Error(
      `exposureControls is undefined. organizationId: ${selected.value.organizationId}`
    );
  }
  const data = structuredClone(exposureControls[selected.value.index]);
  form.value = { ...data };
};

const handleEdit = (organizationId: string, index: number) => {
  selected.value = {
    organizationId,
    index,
  };
  showEditModal.value = true;
  parseToForm();
};

const handleAdd = () => {
  showAddModal.value = true;
  form.value = {
    organization_id: 'ACGIH',
    id: 0,
    sds_id: '',
    sds_product_id: 0,
    material_list: [],
    concentration: null,
    concentration_unit: 'ppm',
    concentration_note: '',
    amount: null,
    amount_unit: 'mg/m3',
    amount_note: '',
    other: '',
    created_at: '',
    updated_at: '',
    deleted_at: null,
    // ACGIH
    category_id: 0,
    property_id: 0,
    // JSOH
    category_dust_id: null,
    category_dust_respirable_id: null,
    category_concentration_id: null,
    // DNEL/DMEL
    category_user_id: null,
    category_term_id: null,
    category_dust_route_id: null,
    category_measurement_id: null,
  };
};

const changeExposureControlDeletedAt = (deletedAt: string | null) => {
  const exposureControls = groupedExposureControlMap.value.get(
    selected.value.organizationId
  );
  if (!exposureControls) {
    throw new Error(
      `exposureControls is undefined. organizationId: ${selected.value.organizationId}`
    );
  }

  const data = structuredClone(exposureControls);
  data[selected.value.index].deleted_at = deletedAt;
  data[selected.value.index].material_list.forEach((material) => {
    material.deleted_at = deletedAt;
  });
  return data;
};

const handleDeleteConfirmButtonClick = () => {
  const data = changeExposureControlDeletedAt(now().toISO());
  const newGroupedExposureControlMap = new Map(groupedExposureControlMap.value);
  newGroupedExposureControlMap.set(selected.value.organizationId, data);
  emits(
    'update:exposureControls',
    [...newGroupedExposureControlMap.values()].flat()
  );
  showDeleteModal.value = false;
  selected.value = { ...selectedInitial };
};

const handleDeleteCancelButtonClick = () => {
  showDeleteModal.value = false;
};

const handleRescueCancelButtonClick = () => {
  showRescueModal.value = false;
};

const handleRescueConfirmButtonClick = () => {
  const data = changeExposureControlDeletedAt(null);
  const newGroupedExposureControlMap = new Map(groupedExposureControlMap.value);
  newGroupedExposureControlMap.set(selected.value.organizationId, data);
  emits(
    'update:exposureControls',
    [...newGroupedExposureControlMap.values()].flat()
  );
  showRescueModal.value = false;
  selected.value = { ...selectedInitial };
};

const closeAddModal = () => {
  showAddModal.value = false;
  form.value = undefined;
};

const closeEditModal = () => {
  showEditModal.value = false;
  selected.value = { ...selectedInitial };
  form.value = undefined;
};

const addExposureControl = () => {
  const data = structuredClone(props.exposureControls);
  if (!form.value) {
    throw new Error('form is undefined');
  }
  data.push(structuredClone(form.value));
  emits('update:exposureControls', data);
  closeAddModal();
};

const editExposureControl = () => {
  const exposureControls = groupedExposureControlMap.value.get(
    selected.value.organizationId
  );
  if (!exposureControls) {
    throw new Error(
      `exposureControls is undefined. organizationId: ${selected.value.organizationId}`
    );
  }
  if (!form.value) {
    throw new Error('form is undefined');
  }
  exposureControls[selected.value.index] = structuredClone(form.value);
  const newGroupedExposureControlMap = new Map(groupedExposureControlMap.value);
  newGroupedExposureControlMap.set(
    selected.value.organizationId,
    exposureControls
  );
  emits(
    'update:exposureControls',
    [...newGroupedExposureControlMap.values()].flat()
  );
  closeEditModal();
};

return { __sfc: true,props, emits, isValidForm, form, groupedExposureControlMap, $t, m, showDeleteModal, showEditModal, showRescueModal, showAddModal, selectedInitial, selected, handleDelete, handleRescue, parseToForm, handleEdit, handleAdd, changeExposureControlDeletedAt, handleDeleteConfirmButtonClick, handleDeleteCancelButtonClick, handleRescueCancelButtonClick, handleRescueConfirmButtonClick, closeAddModal, closeEditModal, addExposureControl, editExposureControl, sharedMessages, OtherTable, SdsSection8ACGIHForm, SdsSection8DNELDMELForm, SdsSection8JSOHForm, AcgihTable, DnelDmelTable, JsohTable, MaterialNameAndCasForm, OrganizationForm, SdsSection8OTHERForm }
}

})